import React from "react";
import { Package2, AlertTriangle } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

/** Returns whether the lead is problematic, full reason (tooltip), one-line reason, and reason count (extra line only when multiple). */
function getProblematicPreview(lead: any, t: (k: string) => string): { isProblematic: boolean; reasonText: string; reasonLine: string; reasonsCount: number } {
  const reasons: string[] = [];
  if (lead.abusiveCommunication) reasons.push(t("leads.abusiveCommunicationLabel"));
  if (lead.redFlags?.length) reasons.push(...lead.redFlags.map((f: any) => f.reason || f.type).filter(Boolean));
  if (lead.problematicReason) {
    const key = "leads.reason" + (lead.problematicReason as string).charAt(0).toUpperCase() + (lead.problematicReason as string).slice(1);
    reasons.push(t(key) || String(lead.problematicReason));
  }
  const isProblematic = reasons.length > 0;
  const reasonLine = reasons.join("; ");
  const reasonText = isProblematic ? `${t("leads.problematic")}: ${reasonLine}` : "";
  return { isProblematic, reasonText, reasonLine, reasonsCount: reasons.length };
}

const getTaskLabel = (task: string): string => {
  const taskLabels: { [key: string]: string } = {
    'update_address': 'Update Address',
    'sell_new_product': 'Sell New Product',
    'collect_more_data': 'Collect More Data',
    'new_dose': 'Resell'
  };
  return taskLabels[task] || task;
};

const getTaskColor = (task: string): string => {
  const taskColors: { [key: string]: string } = {
    'update_address': 'bg-blue-100 text-blue-800 border-blue-200',
    'sell_new_product': 'bg-green-100 text-green-800 border-green-200',
    'collect_more_data': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'new_dose': 'bg-purple-100 text-purple-800 border-purple-200'
  };
  return taskColors[task] || 'bg-gray-100 text-gray-800 border-gray-200';
};


interface NewLeadsPoolTableProps {
  orders: any[];
  currentUser?: {
    id: string;
    username: string;
    role?: string;
    achievements?: string[];
  };
}

const NewLeadsPoolTable: React.FC<NewLeadsPoolTableProps> = ({
  orders,
  currentUser,
}) => {
  const { t } = useLanguage();

  const OrderRow = React.memo(
    ({
      order,
      currentUser,
      t,
    }: {
      order: any;
      currentUser?: { id: string; username: string; role?: string; achievements?: string[] };
      t: (k: string) => string;
    }) => {
      const operatorAchievements = currentUser?.achievements || [];
      const leadTask = order.callCenterTask;
      const hasAchievement = !leadTask || operatorAchievements.includes(leadTask) || currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.role === 'call-center';

      return (
        <tr className="hover:bg-gray-50 transition-colors">
          <td className="px-6 py-4" style={{ maxWidth: "250px" }}>
            {(() => {
              const preview = getProblematicPreview(order, t);
              return (
                <div className="text-sm font-medium text-gray-900 break-words">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{order.name || "N/A"}</span>
                    {preview.isProblematic && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"
                        title={preview.reasonText}
                      >
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        {t("leads.problematic")}
                      </span>
                    )}
                  </div>
                  {preview.isProblematic && preview.reasonLine && (
                    <div className="mt-0.5 text-xs text-amber-800">
                      {t("leads.problematicBecause")}: {preview.reasonLine}
                    </div>
                  )}
                </div>
              );
            })()}
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            {order.callCenterTask ? (
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getTaskColor(order.callCenterTask)}`}>
                  {getTaskLabel(order.callCenterTask)}
                </span>
                {!hasAchievement && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200" title="You haven't achieved this task yet">
                    Locked
                  </span>
                )}
              </div>
            ) : (
              <span className="text-xs text-gray-400">No task</span>
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            {order.callCenterCompletedBy?.username ? (
              <span className="text-sm font-medium text-gray-900">
                {order.callCenterCompletedBy.username.toUpperCase()}
              </span>
            ) : (
              <span className="text-sm text-gray-400">—</span>
            )}
          </td>
        </tr>
      );
    }
  );

  if (orders.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="flex justify-center mb-4">
          <Package2 className="w-16 h-16 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          No New Leads Available
        </h3>
        <p className="text-gray-600">
          All leads have been assigned. Check back later for new leads.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto w-full">
      <table className="w-full min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Task
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('leads.lastOperator')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {orders.map((lead) => (
            <OrderRow
              key={lead.id}
              order={lead}
              currentUser={currentUser}
              t={t}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default NewLeadsPoolTable;
